import { cn } from "@design-system/utils";
import { Slot } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { buttonVariants } from "./button-variants";

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
    loadingLabel?: string;
  };

function Button({
  className,
  variant,
  size,
  asChild = false,
  isLoading = false,
  loadingLabel,
  children,
  disabled,
  onClick,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  // Prevent interactions when loading or disabled
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading || disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    },
    [isLoading, disabled, onClick]
  );

  const content = isLoading ? (
    <span className="relative">
      <span className="invisible">{children}</span>
      <span className="absolute inset-0 flex items-center justify-center">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      </span>
    </span>
  ) : (
    children
  );

  return (
    <Comp
      data-slot="button"
      data-loading={isLoading || undefined}
      disabled={disabled && !isLoading}
      aria-disabled={isLoading || undefined}
      aria-busy={isLoading || undefined}
      aria-label={isLoading && loadingLabel ? loadingLabel : undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      onClick={handleClick}
      {...props}
    >
      {content}
    </Comp>
  );
}

export { Button };
